import * as uiAnalyticsConsumer from '@servicenow/ui-analytics-consumer';
import {mark, types as metrics} from '@servicenow/ui-metrics';
import createSnHttp from './snHttp';
import documentToQueryString from './documentToQueryString';
import {defer} from '@servicenow/ui-internal';
import {checkCacheForResult, shouldDoResultCaching, storeResultInCache} from './resultsCache';
import {getGraphQLDocument} from '../utils/graphQLDocument';
import {
	SPAN_ATTR_GQL_BATCH,
	SPAN_ATTR_GQL_VARIABLES,
	SPAN_TRACE_PARENT,
	SPNA_ATTR_GQL_QUERY
} from '../constants';
const {createTraceParentHeader} = uiAnalyticsConsumer;

const DEFAULT_GQL_ENDPOINT = '/api/now/graphql';
const DEFAULT_BATCH_INTERVAL = 50;
const DEFAULT_MAX_BATCH_SIZE = 10;
const DEFAULT_BATCHING_ENABLED = true;

//The instance does not support streaming GraphQL requests with 'batched' GraphQL requests,
//so double check here again we only get one operation
export function createMultipartConfiguration(operations) {
	if (operations?.length !== 1) return null;

	const {queryContext} = operations[0];
	if (!queryContext?.isStreamingRequest()) return null;

	return {
		multipartConsumer: ({body, headers} = {}) => queryContext.dataReceived(body, headers),
		parsePartsAsJson: true,
		emitCompletedResponse: false
	};
}

//For now we do not allow streaming within streaming when batching, when this is resolved, we can
//remove the 'operation.queryContext.isStreamingRequest()' check
export function canDoSnHttpLevelBatching(operations) {
	if (!Array.isArray(operations)) return;

	let canDoBatching;
	for (var operation of operations) {
		if (operation.__unstableDisableBatching || operation?.queryContext?.isStreamingRequest()) {
			canDoBatching = false;
			break;
		}
	}
	return canDoBatching;
}

export default function createQueryBatcher({
	batchInterval = DEFAULT_BATCH_INTERVAL,
	maxBatchSize = DEFAULT_MAX_BATCH_SIZE,
	batchingEnabled = DEFAULT_BATCHING_ENABLED
} = {}) {
	let queue = [];

	async function enqueue(operation) {
		let deferredResolve, deferredReject;
		const promise = new Promise((resolve, reject) => {
			deferredResolve = resolve;
			deferredReject = reject;
		});
		const batchItem = {
			operation,
			deferredResolve,
			deferredReject
		};

		if (shouldDoResultCaching(operation)) {
			const cacheResult = await checkCacheForResult(operation, batchItem, makeRequest);
			if (cacheResult) return cacheResult;
		}
		if (shouldNotDoGraphQLLevelBatching(operation)) {
			makeRequest([batchItem]);
			return promise;
		}

		queue.push(batchItem);
		if (queue.length === 1) scheduleFlush();

		if (queue.length === maxBatchSize) flushQueue();

		return promise;
	}

	/**
	 * This function determines if this graphQL operation should be batched.
	 * If it defines any custom headers, or batching is not enabled globally,
	 * or the query is known to be unstable, or it is a streaming graphql request,
	 * or it has query params, we do not batch
	 * @param {*} operation
	 * @returns {boolean}
	 */
	function shouldNotDoGraphQLLevelBatching(operation) {
		const {transactionMetaData = {}, headers = [], queryContext} = operation;
		return (
			Object.keys(headers).length ||
			!batchingEnabled ||
			transactionMetaData?.__unstableDisableBatching ||
			queryContext?.isStreamingRequest() ||
			Object.keys(transactionMetaData?.params || {}).length
		);
	}

	function scheduleFlush() {
		defer(() => {
			if (queue.length) flushQueue();
		}, batchInterval);
	}

	function flushQueue() {
		if (!queue.length) return;

		const batchItems = queue;
		queue = [];
		makeRequest(batchItems);
	}

	function makeRequest(batchItems = []) {
		return sendRequestWithCanOmitQuery(batchItems, true);
	}

	async function sendRequestWithCanOmitQuery(batchItems, canOmitQuery) {
		const operations = await Promise.all(
			batchItems.map(async ({operation}) => {
				const {
					operationName,
					variables,
					templateVars,
					domainContext,
					transactionMetaData: {
						cacheable,
						interactionId,
						__unstableDisableBatching,
						__unstableQueryHash,
						query,
						host
					},
					queryContext
				} = operation;
				return {
					operationName,
					query:
						__unstableQueryHash && canOmitQuery
							? ''
							: documentToQueryString(
									getGraphQLDocument(query, templateVars, domainContext, operationName)
							  ),
					variables,
					nowUxInteraction:
						(window.nowUiFramework &&
							(window.nowUiFramework.getUxrInteractionIdByHost?.(host) ||
								window.nowUiFramework.uxrInteractionId)) ||
						null,
					nowUiInteraction: interactionId,
					cacheable,
					__unstableDisableBatching,
					extensions: {
						...(__unstableQueryHash && {
							persistedQuery: {version: 1, sha256Hash: __unstableQueryHash}
						})
					},
					queryContext
				};
			})
		);

		const resolvers = batchItems.map(({deferredResolve, deferredReject}) => ({
			deferredResolve,
			deferredReject
		}));

		const headers = batchItems[0].operation.headers || {};
		if (window.nowUiFramework && window.nowUiFramework.uxrInteractionId)
			headers['NOW-UX-INTERACTION'] = operations[0].nowUxInteraction;

		const mergedParams = {};
		for (let i = 0; i < batchItems.length; i++) {
			const {
				transactionMetaData: {host, interactionId, actionType, params},
				query,
				variables,
				queryContext
			} = batchItems[i].operation;
			const batch =
				operations.length === 1 && operations[0].__unstableDisableBatching ? false : true;
			mark(host, interactionId, metrics.GQL, {
				type: actionType,
				query,
				variables,
				path: DEFAULT_GQL_ENDPOINT,
				batch
			});
			const activeSpan = queryContext?.getActiveSpan();
			if (activeSpan) {
				activeSpan.setAttribute(SPNA_ATTR_GQL_QUERY, query);
				activeSpan.setAttribute(SPAN_ATTR_GQL_VARIABLES, variables);
				activeSpan.setAttribute(SPAN_ATTR_GQL_BATCH, batch);
				const traceParent = createTraceParentHeader(activeSpan.id, interactionId);
				activeSpan.setAttribute(SPAN_TRACE_PARENT, traceParent);
				operations[i].traceparent = traceParent;
			}
			Object.assign(mergedParams, params);
		}

		const snHttpRef =
			(window?.nowUiFramework?.batchGQLEffectRequests && window?.nowUiFramework?.snHttp) ||
			(await createSnHttp());

		return snHttpRef
			.request(DEFAULT_GQL_ENDPOINT, 'POST', {
				//Only send the request in a batch format (in array) if we have more than one operation
				data: operations.length === 1 ? operations[0] : operations,
				headers,
				batch: canDoSnHttpLevelBatching(operations),
				multipartConfig: createMultipartConfiguration(operations),
				params: mergedParams
			})
			.then(async result => {
				return processResult(result, operations, resolvers, batchItems);
			})
			.catch(error => {
				for (let i = 0; i < resolvers.length; i++) {
					const {deferredReject} = resolvers[i];
					deferredReject(error);
				}
			});
	}

	function checkResultForErrors(result, operations) {
		const {data: results, headers = {}} = result;
		// DEF0106841: The graphql endpoint changed their response status code from 401 to 200
		// for when the user is no longer logged in.  This breaks components depending on the
		// 401 error.  This looks for the x-is-logged-in header and throws a pseudo 401 error to
		// remain backwards compatible
		if (headers['x-is-logged-in'] === 'false') {
			const error = new Error();
			error.response = {
				...result,
				status: 401,
				statusText: 'Unauthorized'
			};
			throw error;
		}

		if (operations.length > 1 && results.length !== operations.length) {
			const error = new Error();
			error.response = {
				headers
			};
			throw error;
		}
	}

	function processResult(result, operations, resolvers, batchItems) {
		checkResultForErrors(result, operations);
		const {data, headers = {}} = result;
		let results = data;

		//If we didn't batch our request, convert it back to an array
		if (!Array.isArray(results)) {
			results = [results];
		}

		const returnResult = [];
		for (let i = 0; i < results.length; i++) {
			const currOperation = operations[i];
			const queryContext = currOperation?.queryContext;
			const {deferredResolve} = resolvers[i];

			let currResults = results[i];
			//For streaming queries get the final result from the QueryContext
			if (queryContext?.wasStreamingResponse(headers)) currResults = queryContext?.response;

			const {data = null, errors = []} = currResults || {};

			// need to re-run the entire query because a persisted query wasn't found, run without persisted
			if (errors.some(({message}) => message === 'PersistedQueryNotFound')) {
				queryContext?.resetContext();
				return sendRequestWithCanOmitQuery(batchItems, false);
			}

			if (!errors.length) storeResultInCache(currOperation, currResults, headers);

			returnResult.push({data, errors, headers});
			deferredResolve({data, errors, headers});
		}

		return returnResult;
	}

	return {
		enqueue
	};
}
